/**
 * The global agent panel's UI state (open, and each person's width), persisted
 * per browser so it reopens where and how wide it was. Which agent the panel
 * shows is not stored: it follows the route (`panelAgentForPath`). Thread
 * selection is kept by `useAgentChat` per scope; `pendingThread` carries a deep
 * link (`?agent=&thread=&message=`) to the chat once it mounts. `pendingDraft`
 * carries a message typed into a dashboard composer to the panel chat, which
 * sends it as the first message of a new conversation (the thread is created
 * by that send, so no empty thread is left behind).
 * The width is keyed by uid so people sharing a browser keep their own; the
 * rules (min, max, snaps) are in `panelWidth.ts`.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";

import { PANEL_DEFAULT_WIDTH } from "./panelWidth";
import { agentLabelAt, type ScopeKind } from "./types";
import type { AgentMessageMetadata } from "./useAgentChat";

/**
 * Which of the panel's two chats a request is for: the assistant at a
 * client's focus (on that client's pages) or at the practice focus
 * (everywhere else). One agent; the focus is what differs.
 */
export type PanelAgent = "client" | "practice";

export const isPanelAgent = (v: unknown): v is PanelAgent =>
  v === "client" || v === "practice";

/** The scope kind a panel chat is focused on. */
export const panelFocusKind = (agent: PanelAgent): ScopeKind =>
  agent === "client" ? "client" : "practice";

/** The assistant's label at a panel chat's focus (Casey at every focus). */
export const panelAgentLabel = (agent: PanelAgent): string =>
  agentLabelAt("assistant", panelFocusKind(agent));

/** What `?agent=` has said over time; old notification and handoff links persist. */
const PANEL_AGENT_ALIASES: Record<string, PanelAgent> = {
  client: "client",
  practice: "practice",
  // Pre-merge agent kinds.
  assistant: "client",
  client_manager: "client",
  case_assistant: "client",
  practice_manager: "practice"
};

/**
 * The panel chat a link named (`?agent=`), accepting the pre-merge agent
 * kinds (old notification and handoff links), or null.
 */
export const panelAgentOf = (v: unknown): PanelAgent | null => {
  if (typeof v !== "string" || !v) return null;
  return PANEL_AGENT_ALIASES[v] ?? null;
};

export type OpenRequest = {
  /** The agent whose chat should take the linked thread (defaults to none). */
  agent?: PanelAgent;
  threadId?: string | null;
  messageId?: string | null;
};

export type PendingDraft = {
  agent: PanelAgent;
  text: string;
  /** Sent with the message (the research sources picked in the composer). */
  metadata?: AgentMessageMetadata;
};

type State = {
  open: boolean;
  /** Deep link waiting to be applied by the mounted chat. */
  pendingThread: {
    agent: PanelAgent;
    threadId: string;
    messageId: string | null;
  } | null;
  /** A dashboard message waiting for the panel chat to send it. */
  pendingDraft: PendingDraft | null;
  /** The panel's width in px, per signed-in uid. */
  widthByUid: Record<string, number>;
  /**
   * The panel fills the page beside the app sidebar (Expand Chat). Not
   * persisted: it ends when the panel closes, so Casey never reopens huge.
   */
  expanded: boolean;
};

type Actions = {
  setWidth: (uid: string, width: number) => void;
  setOpen: (open: boolean) => void;
  setExpanded: (expanded: boolean) => void;
  toggleExpanded: () => void;
  toggle: () => void;
  /** Open the panel, optionally on a thread, from a trigger or a link. */
  openWith: (request?: OpenRequest) => void;
  consumePendingThread: (agent: PanelAgent) => State["pendingThread"];
  /** Stash a new conversation's first message; the panel chat sends it. */
  setPendingDraft: (draft: PendingDraft | null) => void;
  /** This agent's draft, cleared once taken. */
  consumePendingDraft: (agent: PanelAgent) => PendingDraft | null;
};

export const AGENT_PANEL_STORAGE_KEY = "agents.panel";

export const useAgentPanelStore = create<State & Actions>()(
  persist(
    (set, get) => ({
      open: false,
      pendingThread: null,
      pendingDraft: null,
      widthByUid: {},
      expanded: false,

      setWidth: (uid, width) =>
        set((s) => ({ widthByUid: { ...s.widthByUid, [uid]: width } })),
      setOpen: (open) => set(open ? { open } : { open, expanded: false }),
      setExpanded: (expanded) => set({ expanded }),
      toggleExpanded: () => set((s) => ({ expanded: !s.expanded })),
      toggle: () =>
        set((s) =>
          s.open ? { open: false, expanded: false } : { open: true }
        ),
      openWith: ({ agent, threadId, messageId } = {}) =>
        set((s) => ({
          open: true,
          pendingThread:
            agent && threadId
              ? { agent, threadId, messageId: messageId ?? null }
              : s.pendingThread
        })),
      consumePendingThread: (agent) => {
        const pending = get().pendingThread;
        if (!pending || pending.agent !== agent) return null;
        set({ pendingThread: null });
        return pending;
      },
      setPendingDraft: (pendingDraft) => set({ pendingDraft }),
      consumePendingDraft: (agent) => {
        const draft = get().pendingDraft;
        if (!draft || draft.agent !== agent) return null;
        set({ pendingDraft: null });
        return draft;
      }
    }),
    {
      name: AGENT_PANEL_STORAGE_KEY,
      partialize: (s) => ({ open: s.open, widthByUid: s.widthByUid })
    }
  )
);

/** The width key for a signed-in uid (one shared key before sign-in resolves). */
export const panelWidthKey = (uid: string | null | undefined): string =>
  uid || "_";

/** The signed-in person's panel width (the default until they resize). */
export const usePanelWidth = (uid: string | null | undefined): number =>
  useAgentPanelStore(
    (s) => s.widthByUid[panelWidthKey(uid)] ?? PANEL_DEFAULT_WIDTH
  );

/** Imperative access for code outside React (notification click handlers). */
export const agentPanelStore = {
  openWith: (request?: OpenRequest) =>
    useAgentPanelStore.getState().openWith(request),
  close: () => useAgentPanelStore.getState().setOpen(false),
  setPendingDraft: (draft: PendingDraft | null) =>
    useAgentPanelStore.getState().setPendingDraft(draft)
};
